'use client';

import React, { useMemo } from 'react';
import Map from '@/components/Map';
import useDeviceDetect from '@/hooks/deviceDetect';
import { ILocation } from '@/interfaces/location';
import { resolveNearestThana, isWithinServiceArea } from '@/helpers/address-helper';
import { StaggerItem } from './StaggerItem';

export interface ExpressLocationMapProps {
  show: boolean;
  pickedLocation: { latitude: number; longitude: number } | null;
  userCurrentLatLong?: { latitude?: number; longitude?: number } | null;
  thanas: ILocation[];
  districts: Array<{ Id: string; ParentId: string }>;
  divisions: Array<{ Id: string }>;
  onPickFromMap: (info: {
    thanaId: string;
    districtId: string;
    divisionId: string;
    lat: number;
    lng: number;
    name: string;
    postalCode: string;
    invalidPick: boolean;
  }) => void;
}

const MOBILE_MAP_OFFSET = 360;
const MOBILE_MAP_MIN_HEIGHT = 160;
const MOBILE_MAP_MAX_HEIGHT = 420;

const ExpressLocationMap = ({
  show,
  pickedLocation,
  userCurrentLatLong,
  thanas,
  districts,
  divisions,
  onPickFromMap,
}: ExpressLocationMapProps) => {
  const { isMobile, windowSize } = useDeviceDetect();

  // Mobile map height scales with viewport height — taller phones get a
  // bigger map. Floor at MOBILE_MAP_MIN_HEIGHT so short-landscape viewports
  // don't collapse; cap at MOBILE_MAP_MAX_HEIGHT for desktop parity.
  const mapHeight = useMemo(() => {
    if (!isMobile || windowSize.height <= 0) {
      return undefined;
    }
    const target = windowSize.height - MOBILE_MAP_OFFSET;
    return `${Math.max(MOBILE_MAP_MIN_HEIGHT, Math.min(MOBILE_MAP_MAX_HEIGHT, target))}px`;
  }, [isMobile, windowSize.height]);
  const resolvedThana = useMemo(() => {
    if (!pickedLocation) {
      return null;
    }
    return resolveNearestThana(pickedLocation.latitude, pickedLocation.longitude, thanas);
  }, [pickedLocation, thanas]);

  const centroids = useMemo(() => {
    return thanas
      .map((t) => ({
        Id: t.Id,
        Name: t.Name,
        Longitude: Number(t.Long),
        Latitude: Number(t.Lat),
        selected: resolvedThana?.Id === t.Id,
      }))
      .filter((c) => Number.isFinite(c.Longitude) && Number.isFinite(c.Latitude));
  }, [thanas, resolvedThana]);

  // Popup shows supplementary details only (Postal Code). Hidden if none.
  const pickedPopup = useMemo(() => {
    if (!resolvedThana || !pickedLocation) {
      return null;
    }
    const lines: string[] = [];
    if (resolvedThana.PostalCode && resolvedThana.PostalCode.trim() !== '') {
      lines.push(`Postal Code: ${resolvedThana.PostalCode}`);
    }
    if (lines.length === 0) {
      return null;
    }
    return {
      isVisible: true,
      render: () => (
        <>
          {lines.map((line, idx) => (
            <p key={idx}>{line}</p>
          ))}
        </>
      ),
    };
  }, [resolvedThana, pickedLocation]);

  const mapPickedLocation = pickedLocation
    ? { longitude: pickedLocation.longitude, latitude: pickedLocation.latitude }
    : null;

  const mapInitialCenter = useMemo(() => {
    if (pickedLocation) {
      return { longitude: pickedLocation.longitude, latitude: pickedLocation.latitude, zoom: 13 };
    }
    if (
      userCurrentLatLong &&
      Number.isFinite(userCurrentLatLong.latitude) &&
      Number.isFinite(userCurrentLatLong.longitude)
    ) {
      return {
        longitude: userCurrentLatLong.longitude as number,
        latitude: userCurrentLatLong.latitude as number,
        zoom: 13,
      };
    }
    return undefined;
  }, [pickedLocation, userCurrentLatLong]);

  const handleMapClick = (coords: { latitude: number; longitude: number }) => {
    // Data-independent gate: reject clicks outside Bangladesh first.
    if (!isWithinServiceArea(coords.latitude, coords.longitude)) {
      onPickFromMap({
        thanaId: '',
        districtId: '',
        divisionId: '',
        lat: coords.latitude,
        lng: coords.longitude,
        name: '',
        postalCode: '',
        invalidPick: true,
      });
      return;
    }
    const nearest = resolveNearestThana(coords.latitude, coords.longitude, thanas);
    if (!nearest) {
      onPickFromMap({
        thanaId: '',
        districtId: '',
        divisionId: '',
        lat: coords.latitude,
        lng: coords.longitude,
        name: '',
        postalCode: '',
        invalidPick: true,
      });
      return;
    }
    const district = districts.find((d) => d.Id === nearest.ParentId);
    const division = district ? divisions.find((v) => v.Id === district.ParentId) : undefined;
    onPickFromMap({
      thanaId: nearest.Id,
      districtId: district?.Id ?? '',
      divisionId: division?.Id ?? '',
      lat: coords.latitude,
      lng: coords.longitude,
      name: nearest.Name,
      postalCode: nearest.PostalCode,
      invalidPick: false,
    });
  };

  return (
    <StaggerItem
      show={show}
      delayMs={120}
      className="express-map-wrap h-[260px] w-full max-w-full overflow-hidden rounded border border-gray-200 sm:h-[400px] sm:w-full sm:max-w-full md:h-[420px]"
      style={mapHeight ? { height: mapHeight } : undefined}
    >
      <Map
        pickedLocation={mapPickedLocation}
        initialCenter={mapInitialCenter}
        onMapClick={handleMapClick}
        pickOnClick={true}
        pickedPopup={pickedPopup}
        centroids={centroids}
      />
      <style jsx global>{`
        /* Floor the mobile map at 160px so short-landscape viewports don't collapse. */
        @media (max-width: 639px) {
          .express-map-wrap {
            min-height: 160px;
          }
        }
      `}</style>
    </StaggerItem>
  );
};

export default ExpressLocationMap;
